import { useEffect, useId, useRef, useState } from 'react';
import {
  PRODUCT_BARCODE_ERROR,
  PRODUCT_BARCODE_HINT,
  isValidProductBarcode,
  normalizeProductBarcode,
} from '@/utils/productBarcode';
import { INTERNAL_BARCODE_UNAVAILABLE } from '@/utils/internalBarcode';
import type { BarcodeOwner } from '@/infrastructure/ProductAttributesClient';
import { asciiScanField } from './asciiScanField';
import { usePromptDialog } from './usePromptDialog';
import { BarcodeOwnerCard } from './BarcodeOwnerCard';

export interface BarcodeCellProps {
  sku: string;
  /** Names the product in the replace confirmation; the sku stands in where it is absent. */
  productName?: string | undefined;
  /** From the catalog, or from a save this session; absent means the product has never had one. */
  barcode: string | undefined;
  productId: number | undefined;
  /** Answers the product that already carries the code when the save is refused for that (SSD-7532). */
  onSave: (productId: number, barcode: string) => Promise<BarcodeOwner | void>;
  /** A locked order's table: the box shows but takes nothing. */
  disabled?: boolean;
  /** A known barcode can be replaced too, for a catalog code that does not match the packaging (SSD-7541). */
  editable?: boolean;
  /** Called once an edit closes with the cursor left nowhere, so a scan desk can take it back. */
  onEditClosed?: () => void;
  /**
   * Gives a product with no barcode an internal code of its own (SSD-7611): `code` is the one its SKU makes, null
   * when the SKU cannot make one, and `onMake` saves it (and prints its sticker once saved). A product that already
   * carries its internal code can print the sticker again with `onPrint`. Without it the cell offers neither button.
   */
  makeCode?: MakeCode;
}

export interface MakeCode {
  code: string | null;
  onMake: (productId: number, code: string) => Promise<BarcodeOwner | void>;
  /** Prints the sticker again: a print that failed or a sticker that came off leaves the code with no label otherwise. */
  onPrint: (code: string) => Promise<void>;
}

/**
 * The barcode column. A product the catalog already knows reads as a ticked number; one it does not
 * carries an always-visible input, because the value typed here is what makes the next scan resolve.
 * Saved against the product master, not the order — the same gap follows the sku onto every order.
 * Where `editable`, a click on a known code opens the same input to scan the packaging's code over it.
 */
export function BarcodeCell({
  sku,
  productName,
  barcode,
  productId,
  onSave,
  disabled = false,
  editable = false,
  onEditClosed,
  makeCode,
}: BarcodeCellProps) {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [printing, setPrinting] = useState(false);
  // A save that went through hands back the new code, which is what closes the edit; a refusal leaves it open.
  const [shownBarcode, setShownBarcode] = useState(barcode);
  const [refused, setRefused] = useState<{ barcode: string; owner: BarcodeOwner } | null>(null);
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const currentBarcode = useRef(barcode);
  currentBarcode.current = barcode;
  // Each product/barcode pair, and each edit closed on it, is its own generation; a save's answer only lands
  // on the generation it was sent from, so a refusal that comes back after the code moved on shows no card.
  const generation = useRef({ productId, barcode, count: 0 });
  if (generation.current.productId !== productId || generation.current.barcode !== barcode) {
    generation.current = { productId, barcode, count: generation.current.count + 1 };
  }
  const { askConfirm, dialog } = usePromptDialog();
  const invalid = draft !== '' && !isValidProductBarcode(draft);

  // The edit's input leaves the page as it closes, and a cursor left nowhere would drop the next scan. A field
  // the operator moved to while the save was out (the Shelf) keeps the cursor.
  const wasEditing = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (wasEditing.current && !editing && (active === null || active === document.body)) {
      onEditClosed?.();
    }
    wasEditing.current = editing;
  }, [editing, onEditClosed]);

  // The input is disabled while a save is out, which drops the cursor; a refused save hands it back for a rescan.
  const wasSaving = useRef(false);
  useEffect(() => {
    const active = document.activeElement;
    if (wasSaving.current && !saving && inputRef.current && (active === null || active === document.body)) {
      inputRef.current.focus();
    }
    wasSaving.current = saving;
  }, [saving]);

  if (barcode !== shownBarcode) {
    setShownBarcode(barcode);
    setEditing(false);
    setDraft('');
    setRefused(null);
  }

  const cancelEdit = () => {
    generation.current = { ...generation.current, count: generation.current.count + 1 };
    setEditing(false);
    setDraft('');
    setRefused(null);
  };

  if (barcode && !editing) {
    // The product's own internal code can print its sticker again, on a line of its own under the code (SSD-7611).
    const reprint =
      makeCode && makeCode.code === barcode ? (
        <button
          type="button"
          className="btn btn-line btn-sm barcode-reprint"
          aria-label={`Print the label for ${barcode}`}
          title="Prints this product's code label again"
          disabled={disabled || printing}
          onClick={() => {
            setPrinting(true);
            void makeCode.onPrint(barcode).finally(() => setPrinting(false));
          }}
        >
          Print label
        </button>
      ) : null;
    // The code itself is the control: the column is sized to the check and 13 digits, with no room for a button.
    // A locked order's table shows the code but does not open it for a change.
    if (editable && !disabled && productId != null) {
      return (
        <>
          <button
            type="button"
            className="barcode-known barcode-edit num"
            aria-label={`Change barcode ${barcode} for ${sku}`}
            title="Change barcode"
            onClick={() => setEditing(true)}
          >
            <span aria-hidden="true">✓</span>
            <span>{barcode}</span>
          </button>
          {reprint}
          {dialog}
        </>
      );
    }
    return (
      <>
        <span className="barcode-known num">
          <span aria-hidden="true">✓</span>
          <span>{barcode}</span>
        </span>
        {reprint}
        {dialog}
      </>
    );
  }

  const save = async () => {
    if (disabled || productId == null || !isValidProductBarcode(draft) || saving) {
      return;
    }
    // Saving the code the product already has changes nothing, so it only closes the edit.
    if (draft === barcode) {
      cancelEdit();
      return;
    }
    // The product master's code follows the sku onto every order, so overwriting one is confirmed first.
    if (editing && barcode) {
      const agreed = await askConfirm({
        title: 'Replace barcode?',
        message: `${productName ?? sku}: ${barcode} → ${draft}`,
        confirmLabel: 'Replace',
      });
      // A code that changed under the open question is no longer the one the operator agreed to replace.
      if (!agreed || currentBarcode.current !== barcode) {
        cancelEdit();
        return;
      }
    }
    setSaving(true);
    const sentFrom = generation.current.count;
    try {
      const owner = await onSave(productId, draft);
      if (generation.current.count === sentFrom) {
        setRefused(owner ? { barcode: draft, owner } : null);
      }
    } finally {
      setSaving(false);
    }
  };

  const make = async () => {
    if (!makeCode?.code || disabled || productId == null || saving) {
      return;
    }
    setDraft('');
    setSaving(true);
    const sentFrom = generation.current.count;
    try {
      const owner = await makeCode.onMake(productId, makeCode.code);
      if (generation.current.count === sentFrom) {
        setRefused(owner ? { barcode: makeCode.code, owner } : null);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="barcode-entry">
      <input
        ref={inputRef}
        className="bcin num"
        aria-label={editing ? `New barcode for ${sku}` : `Barcode for ${sku}`}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? errorId : undefined}
        // The code being replaced stays in sight until the new one is scanned over it.
        // The rule for a new code was cut in Inventory's barcode box ("Letters, digits or - (up t"), so the box says
        // what to do and the rule is the tooltip (SSD-7674).
        placeholder={editing ? barcode : 'Scan or type'}
        title={PRODUCT_BARCODE_HINT}
        value={draft}
        disabled={saving || disabled}
        autoFocus={editing}
        // A barcode with letters is scanned in too, so Korean-mode Hangul is mapped back to the keys it came from.
        {...asciiScanField(
          (value) => {
            setDraft(normalizeProductBarcode(value));
            // A new code is a new attempt; the card was about the one refused.
            setRefused(null);
          },
          (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void save();
            } else if (event.key === 'Escape' && editing) {
              event.preventDefault();
              cancelEdit();
            }
          },
        )}
      />
      <button
        type="button"
        className="btn btn-green btn-sm"
        aria-label={`Save barcode for ${sku}`}
        disabled={saving || disabled || productId == null || !isValidProductBarcode(draft)}
        onClick={() => void save()}
      >
        ✓
      </button>
      {editing && (
        <button
          type="button"
          className="btn btn-line btn-sm"
          aria-label={`Keep barcode ${barcode} for ${sku}`}
          disabled={saving}
          onClick={cancelEdit}
        >
          ✕
        </button>
      )}
      {/* A product with no code of its own gets one, on its own line so the column keeps its width (SSD-7611). */}
      {makeCode && !editing && (
        <button
          type="button"
          className="btn btn-line btn-sm barcode-make"
          aria-label={`Make a barcode for ${sku}`}
          title={
            !makeCode.code
              ? INTERNAL_BARCODE_UNAVAILABLE
              : productId == null
                ? 'This product is not in the catalog yet'
                : `Saves ${makeCode.code} as this product's barcode and prints its label`
          }
          disabled={saving || disabled || productId == null || !makeCode.code}
          onClick={() => void make()}
        >
          Make code
        </button>
      )}
      {invalid && (
        <div id={errorId} className="text-danger" role="alert">
          {PRODUCT_BARCODE_ERROR}
        </div>
      )}
      {dialog}
      {refused && <BarcodeOwnerCard barcode={refused.barcode} owner={refused.owner} />}
    </div>
  );
}
